// NTA eligibility thresholds (spec 5.1, T-01..T-08) — admin form, validation, CSV. Pure.
// A blank threshold means "no constraint". open-to-new (T-08) is not here: it is
// bv_site_network.open_to_new_applicants, edited on the Network Tariffs tab.

export type NtaRule = {
  id?: string;
  distributor: string;
  tariff_code: string;
  effective_from: string;          // YYYY-MM-DD
  effective_to: string | null;
  classifications: string[] | null;     // T-01
  connection_voltages: string[] | null; // T-02
  min_demand: number | null;            // T-03, inclusive
  max_demand: number | null;            // T-04, inclusive
  demand_unit: 'kVA' | 'kW' | null;
  min_usage_kwh: number | null;         // T-05, rolling 12 months
  max_usage_kwh: number | null;         // T-06
  tariff_type: string | null;           // T-07
  export_rule: NtaExportRule;           // D-03 export gate
  source_document: string | null;
  // Distributor threshold sheet fields (migration 159).
  class_min: string | null;             // customer-class range, NTA_CUSTOMER_CLASSES
  class_max: string | null;
  tariff_role: 'primary' | 'secondary' | null; // secondary (controlled load, solar) is never an alternative
  location: string | null;              // e.g. CBD
  usage_profile: string | null;
  demand_profile: string | null;
  source_text: string | null;           // the sheet's eligibility wording
  review_flags: string[] | null;        // what the importer could not apply
};

/** Select list for bv_nta_eligibility_rule, shared by the admin service and the engine. */
export const NTA_RULE_COLUMNS =
  'id, distributor, tariff_code, effective_from, effective_to, classifications, connection_voltages, min_demand, ' +
  'max_demand, demand_unit, min_usage_kwh, max_usage_kwh, tariff_type, export_rule, source_document, class_min, class_max, ' +
  'tariff_role, location, usage_profile, demand_profile, source_text, review_flags';

export type NtaExportRule = 'required' | 'not_allowed' | null;

/** MSATS NMI classification codes (T-01). */
export const NTA_CLASSIFICATIONS = ['SMALL', 'LARGE', 'BULK', 'WHOLESAL', 'DWHOLSAL', 'GENERATR', 'INTERCON', 'NREG', 'XBOUNDRY'];
/** Connection configuration (T-02). ponytail: proposed list — Choice Energy to enumerate (D-01). */
export const NTA_VOLTAGES = ['LV', 'HV', 'SUBT', 'TRANS'];
/** Customer classes on the distributor sheet. The first four are ordered (a range); the rest match exactly. */
export const NTA_CUSTOMER_CLASSES = ['Residential', 'Small', 'Medium', 'Large', 'Sub-Transmission Storage', 'Parent Network', 'Public Equipment'];
const CLASS_RANK = ['RESIDENTIAL', 'SMALL', 'MEDIUM', 'LARGE'];
/** Sheet column L -> NTA_VOLTAGES. */
export const NTA_CONNECTION_CONFIG: Record<string, string> = { 'LV DISTRIBUTION': 'LV', 'HV DISTRIBUTION': 'HV', SUBTRANSMISSION: 'SUBT' };

/** "Large Business" / "medium" -> "Large" / "Medium"; unknown -> null. */
export function normaliseClass(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim().toUpperCase();
  return NTA_CUSTOMER_CLASSES.find((c) => t === c.toUpperCase() || t.split(/\s+/)[0] === c.toUpperCase()) ?? null;
}

export type ClassRange = { min: string; max: string };
/** The rule's range; one bound alone means that class only. Null = no class rule. */
export function ruleClassRange(r: Pick<NtaRule, 'class_min' | 'class_max'>): ClassRange | null {
  const min = normaliseClass(r.class_min) ?? normaliseClass(r.class_max);
  const max = normaliseClass(r.class_max) ?? min;
  return min && max ? { min, max } : null;
}

/** Do two class ranges share a class? Ranked classes compare by order, others by name. */
export function classRangesOverlap(a: ClassRange, b: ClassRange): boolean {
  const ra = [CLASS_RANK.indexOf(a.min.toUpperCase()), CLASS_RANK.indexOf(a.max.toUpperCase())];
  const rb = [CLASS_RANK.indexOf(b.min.toUpperCase()), CLASS_RANK.indexOf(b.max.toUpperCase())];
  if ([...ra, ...rb].some((x) => x < 0)) return [a.min, a.max].some((x) => x === b.min || x === b.max);
  return ra[0] <= rb[1] && rb[0] <= ra[1];
}

/** Tariff structures observed in the Energetica output (T-07). Choice Energy to confirm the controlled list. */
export const NTA_TARIFF_TYPES = ['TOU', 'TOU + demand', 'Anytime energy + demand', 'Monthly peak demand', 'Annual peak demand'];

export const NTA_RULE_CSV_HEADERS = [
  'distributor', 'tariff_code', 'effective_from', 'effective_to', 'classifications', 'connection_voltages',
  'min_demand', 'max_demand', 'demand_unit', 'min_usage_kwh', 'max_usage_kwh', 'tariff_type', 'export_rule',
  'source_document', 'class_min', 'class_max', 'tariff_role', 'location', 'usage_profile', 'demand_profile', 'source_text',
] as const;

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Every problem with a rule, as user-facing sentences. `others` = the org's other rules. */
export function validateRule(r: NtaRule, others: NtaRule[] = []): string[] {
  const e: string[] = [];
  if (!r.distributor.trim()) e.push('Distributor is required');
  if (!r.tariff_code.trim()) e.push('Tariff code is required');
  if (!DATE.test(r.effective_from)) e.push('Effective from must be a date (YYYY-MM-DD)');
  if (r.effective_to != null && !DATE.test(r.effective_to)) e.push('Effective to must be a date (YYYY-MM-DD) or blank');
  if (r.effective_to && DATE.test(r.effective_from) && r.effective_to < r.effective_from) {
    e.push('Effective to is before effective from');
  }
  for (const [k, v] of Object.entries({
    'Min demand': r.min_demand, 'Max demand': r.max_demand, 'Min usage': r.min_usage_kwh, 'Max usage': r.max_usage_kwh,
  })) {
    if (v != null && (!Number.isFinite(v) || v < 0)) e.push(`${k} must be a number ≥ 0`);
  }
  if (r.min_demand != null && r.max_demand != null && r.min_demand > r.max_demand) e.push('Min demand is above max demand');
  if (r.min_usage_kwh != null && r.max_usage_kwh != null && r.min_usage_kwh > r.max_usage_kwh) e.push('Min usage is above max usage');
  if ((r.min_demand != null || r.max_demand != null) && !r.demand_unit) e.push('Demand unit (kVA or kW) is required with a demand threshold');
  for (const [k, v] of [['Class from', r.class_min], ['Class to', r.class_max]] as const) {
    if (v && !normaliseClass(v)) e.push(`${k} "${v}" is not one of ${NTA_CUSTOMER_CLASSES.join(', ')}`);
  }
  const lo = CLASS_RANK.indexOf(normaliseClass(r.class_min)?.toUpperCase() ?? '');
  const hi = CLASS_RANK.indexOf(normaliseClass(r.class_max)?.toUpperCase() ?? '');
  if (lo >= 0 && hi >= 0 && lo > hi) e.push('Class from is above class to');

  const end = (x: NtaRule) => x.effective_to ?? '9999-12-31';
  // Unsaved rules (CSV rows) have no id, so "is this the same rule" needs an id on r.
  const clash = others.find((o) => o !== r && (r.id == null || o.id !== r.id)
    && o.distributor === r.distributor && o.tariff_code.toUpperCase() === r.tariff_code.toUpperCase()
    && o.effective_from <= end(r) && r.effective_from <= end(o));
  if (clash) {
    e.push(`Overlaps the rule effective ${clash.effective_from} – ${clash.effective_to ?? 'open'} for this tariff`);
  }
  return e;
}

/** The rule in force for a tariff on a date, or null. */
export function ruleOn(rules: NtaRule[], distributor: string, code: string, onDate: string): NtaRule | null {
  return rules.find((r) => r.distributor === distributor && r.tariff_code.toUpperCase() === code.toUpperCase()
    && r.effective_from <= onDate && (r.effective_to == null || onDate <= r.effective_to)) ?? null;
}

/** Plain-English summary of a rule's thresholds for the table. */
export function describeRule(r: NtaRule): string[] {
  const out: string[] = [];
  // Pinned to en-AU so the browser's locale can't turn 160000 into "1,60,000".
  const fmt = (n: number) => n.toLocaleString('en-AU');
  const range = (lo: number | null, hi: number | null, unit: string) =>
    lo != null && hi != null ? `${fmt(lo)}–${fmt(hi)} ${unit}`
      : lo != null ? `≥ ${fmt(lo)} ${unit}` : `≤ ${fmt(hi!)} ${unit}`;
  if (r.min_usage_kwh != null || r.max_usage_kwh != null) out.push(`Usage ${range(r.min_usage_kwh, r.max_usage_kwh, 'kWh/yr')}`);
  if (r.min_demand != null || r.max_demand != null) out.push(`Demand ${range(r.min_demand, r.max_demand, r.demand_unit ?? '')}`);
  const cr = ruleClassRange(r);
  if (cr) out.push(cr.min === cr.max ? cr.min : `${cr.min}–${cr.max}`);
  if (r.classifications?.length) out.push(`Class ${r.classifications.join('/')}`);
  if (r.connection_voltages?.length) out.push(`Voltage ${r.connection_voltages.join('/')}`);
  if (r.tariff_type) out.push(r.tariff_type);
  if (r.export_rule === 'required') out.push('Export required');
  if (r.export_rule === 'not_allowed') out.push('No export');
  if (r.location) out.push(`${r.location} only`);
  if (r.tariff_role === 'secondary') out.push('Secondary');
  if (r.review_flags?.length) out.push(`${r.review_flags.length} to review`);
  return out;
}

// ─── CSV ──────────────────────────────────────────────────────────────────────

const csvCell = (v: unknown): string => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function rulesToCsv(rules: NtaRule[]): string {
  const rows = rules.map((r) => [
    r.distributor, r.tariff_code, r.effective_from, r.effective_to, r.classifications?.join('|'),
    r.connection_voltages?.join('|'), r.min_demand, r.max_demand, r.demand_unit, r.min_usage_kwh, r.max_usage_kwh,
    r.tariff_type, r.export_rule, r.source_document, r.class_min, r.class_max, r.tariff_role, r.location,
    r.usage_profile, r.demand_profile, r.source_text,
  ].map(csvCell).join(','));
  return [NTA_RULE_CSV_HEADERS.join(','), ...rows].join('\n');
}

/** RFC-4180-ish: quoted fields, doubled quotes, CRLF or LF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ''));
}

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s.replace(/,/g, '')));
const listOrNull = (s: string): string[] | null => {
  const xs = s.split('|').map((x) => x.trim().toUpperCase()).filter(Boolean);
  return xs.length ? xs : null;
};

export type ParsedRules = { rules: NtaRule[]; errors: Array<{ line: number; error: string }> };

/** CSV text -> rules. Each row is validated alone and against the rows above it. */
export function parseRulesCsv(text: string): ParsedRules {
  const [header, ...body] = parseCsv(text);
  const out: ParsedRules = { rules: [], errors: [] };
  if (!header) return { rules: [], errors: [{ line: 1, error: 'File is empty' }] };
  const ix = new Map(header.map((h, i) => [h.trim().toLowerCase(), i]));
  const missing = ['distributor', 'tariff_code', 'effective_from'].filter((h) => !ix.has(h));
  if (missing.length) return { rules: [], errors: [{ line: 1, error: `Missing column(s): ${missing.join(', ')}` }] };
  const get = (r: string[], k: string) => (ix.has(k) ? (r[ix.get(k)!] ?? '').trim() : '');

  body.forEach((r, i) => {
    const unit = get(r, 'demand_unit');
    const exp = get(r, 'export_rule').toLowerCase().replace(/[\s-]/g, '_');
    const role = get(r, 'tariff_role').toLowerCase();
    const rule: NtaRule = {
      distributor: get(r, 'distributor'),
      tariff_code: get(r, 'tariff_code').toUpperCase(),
      effective_from: get(r, 'effective_from'),
      effective_to: get(r, 'effective_to') || null,
      classifications: listOrNull(get(r, 'classifications')),
      connection_voltages: listOrNull(get(r, 'connection_voltages')),
      min_demand: numOrNull(get(r, 'min_demand')),
      max_demand: numOrNull(get(r, 'max_demand')),
      demand_unit: unit.toLowerCase() === 'kva' ? 'kVA' : unit.toLowerCase() === 'kw' ? 'kW' : null,
      min_usage_kwh: numOrNull(get(r, 'min_usage_kwh')),
      max_usage_kwh: numOrNull(get(r, 'max_usage_kwh')),
      tariff_type: get(r, 'tariff_type') || null,
      // requires_export (Y/N) is the pre-159 column; still read so old files import.
      export_rule: exp === 'required' || exp === 'not_allowed' ? exp
        : /^(y|yes|true|1)$/i.test(get(r, 'requires_export')) ? 'required' : null,
      source_document: get(r, 'source_document') || null,
      class_min: normaliseClass(get(r, 'class_min')) ?? (get(r, 'class_min') || null),
      class_max: normaliseClass(get(r, 'class_max')) ?? (get(r, 'class_max') || null),
      tariff_role: role === 'primary' || role === 'secondary' ? role : null,
      location: get(r, 'location') || null,
      usage_profile: get(r, 'usage_profile') || null,
      demand_profile: get(r, 'demand_profile') || null,
      source_text: get(r, 'source_text') || null,
      review_flags: null,
    };
    const errs = validateRule(rule, out.rules);
    if (unit && !rule.demand_unit) errs.push(`Demand unit "${unit}" must be kVA or kW`);
    if (exp && !rule.export_rule) errs.push(`Export rule "${get(r, 'export_rule')}" must be required or not_allowed`);
    if (role && !rule.tariff_role) errs.push(`Tariff role "${get(r, 'tariff_role')}" must be primary or secondary`);
    if (errs.length) out.errors.push({ line: i + 2, error: errs.join('; ') });
    else out.rules.push(rule);
  });
  return out;
}

export type TariffCoverage = {
  distributor: string;
  tariffCode: string;
  tariffName: string | null;
  openToNew: boolean;
  rule: NtaRule | null;
};

/** One row per distinct (distributor, tariff_code) in bv_site_network, with its rule in force on `onDate`. */
export function tariffCoverage(
  networkRows: Array<{ distributor: string | null; tariff_code: string; tariff_name: string | null; open_to_new_applicants: boolean | null }>,
  rules: NtaRule[],
  onDate: string,
): TariffCoverage[] {
  const by = new Map<string, TariffCoverage>();
  for (const n of networkRows) {
    if (!n.distributor || !n.tariff_code) continue;
    const key = `${n.distributor}|${n.tariff_code.toUpperCase()}`;
    const prev = by.get(key);
    if (prev) {
      prev.tariffName ??= n.tariff_name;
      if (n.open_to_new_applicants === false) prev.openToNew = false;
      continue;
    }
    by.set(key, {
      distributor: n.distributor,
      tariffCode: n.tariff_code.toUpperCase(),
      tariffName: n.tariff_name,
      openToNew: n.open_to_new_applicants !== false,
      rule: ruleOn(rules, n.distributor, n.tariff_code, onDate),
    });
  }
  return [...by.values()].sort((a, b) => a.distributor.localeCompare(b.distributor) || a.tariffCode.localeCompare(b.tariffCode));
}
